import * as React from "react";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { Input } from "../ui/input";

export function MultiSelect({
    options,
    selected = [],
    onChange,
    placeholder = "Select...",
    searchPlaceholder = "Search...",
    className = "",
    disabled = false
}) {
    const [open, setOpen] = React.useState(false);
    const [searchTerm, setSearchTerm] = React.useState("");

    const filteredOptions = options.filter((option) => option.label.toLowerCase().includes(searchTerm.toLowerCase()));

    const handleToggle = (value) => {
        if (selected.includes(value)) {
            onChange(selected.filter((v) => v !== value));
        } else {
            onChange([...selected, value]);
        }
    };

    const selectedLabels = options.filter((opt) => selected.includes(opt.value)).map((opt) => opt.label);

    return (
        <Popover open={open && !disabled} onOpenChange={disabled ? undefined : setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    className={`w-full justify-between min-h-10 ${className} ${
                        disabled ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                    disabled={disabled}
                    type="button"
                >
                    <span className="truncate flex-1 text-left mr-2 min-w-0">
                        {selectedLabels.length > 0 ? (
                            selectedLabels.length > 2 
                                ? `${selectedLabels.slice(0, 2).join(", ")}...`
                                : selectedLabels.join(", ")
                        ) : (
                            <span className="text-muted-foreground">{placeholder}</span>
                        )}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent 
                className="w-[var(--radix-popover-trigger-width)] p-0 max-h-[300px] flex flex-col" 
                align="start"
                side="bottom"
                collisionPadding={8}
            >
                <div className="p-2 flex-shrink-0">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                            placeholder={searchPlaceholder}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10"
                            disabled={disabled}
                        />
                    </div>
                </div>
                <div 
                    className="max-h-[200px] overflow-y-auto py-2 flex-1 min-h-0 scrollbar-thin"
                    onWheel={(e) => {
                        // Prevent scroll from propagating to parent when scrolling within dropdown
                        e.stopPropagation();
                    }}
                    onMouseDown={(e) => {
                        // Prevent click from closing dropdown when clicking on scrollbar
                        e.stopPropagation();
                    }}
                    style={{ 
                        overscrollBehavior: 'contain',
                        WebkitOverflowScrolling: 'touch'
                    }}
                >
                    {filteredOptions.map((option) => (
                        <div
                            key={option.value}
                            className={`flex items-center px-3 py-2 ${
                                disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:bg-accent"
                            }`}
                            onClick={disabled ? undefined : () => handleToggle(option.value)}
                        >
                            <Check
                                className={`mr-2 h-4 w-4 shrink-0 ${
                                    selected.includes(option.value) ? "opacity-100" : "opacity-0"
                                }`}
                            />
                            <span className="truncate flex-1">{option.label}</span>
                            {option.count !== undefined && (
                                <span className="ml-2 text-xs text-muted-foreground shrink-0">
                                    {option.count.toLocaleString()}
                                </span>
                            )}
                        </div>
                    ))}
                    {filteredOptions.length === 0 && (
                        <div className="px-3 py-2 text-muted-foreground text-sm">
                            {searchTerm ? "No items found." : "No items available."}
                        </div>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
}
